import React, { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import ContactModal from "../components/ContactModal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Calendar, 
  CheckCircle, 
  ArrowRight, 
  Zap, 
  Users, 
  Target, 
  TrendingUp, 
  Shield,
  Award,
  Lightbulb,
  Settings,
  BarChart3,
  Code,
  Database,
  Lock,
  Globe,
  ShoppingCart,
  MessageSquare,
  Brain,
  Cog
} from "lucide-react";

const seoConfig = {
  title: "KI-Anwendungen | Custom AI Solutions | BROVKO",
  description: "Maßgeschneiderte KI-Anwendungen für Ihr Unternehmen. Private LLMs, KI-Assistenten, Prozessautomatisierung und intelligente Analyse-Tools.",
  keywords: "KI-Anwendungen, Custom AI, Private LLM, KI-Assistent, Prozessautomatisierung, KI-Tools",
  canonicalUrl: "/leistungsangebot/ki-anwendungen"
};

const KIAnwendungen = () => {
  const { t } = useLanguage();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Modal handlers
  const openContactModal = (buttonContext = null, buttonText = null) => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  const applications = [
    {
      icon: Lock,
      title: "Private LLMs",
      subtitle: "Unternehmensspezifische KI mit maximaler Sicherheit",
      features: [
        "Aufbau unternehmenseigener Sprachmodelle für interne Prozesse",
        "Vollständige Einhaltung der DSGVO und anderer Compliance-Anforderungen",
        "Sicherstellung, dass Unternehmensdaten nicht an Dritte gelangen"
      ],
      benefits: ["100% Datenschutz", "DSGVO-konform", "Keine Datenübertragung"]
    },
    {
      icon: Globe,
      title: "KI-gestützte Unternehmensplattformen",
      subtitle: "Zukunftssichere Geschäftsmodelle",
      features: [
        "Entwicklung skalierbarer KI-Lösungen für Geschäftsprozesse",
        "Aufbau intelligenter Automatisierungen für mehr Effizienz",
        "Verbesserung von Datenqualität, Analysen und Entscheidungsfindung"
      ],
      benefits: ["Skalierbare Lösungen", "Intelligente Automatisierung", "Bessere Entscheidungen"]
    },
    {
      icon: Cog,
      title: "Erweiterung bestehender Plattformen",
      subtitle: "KI-Komponenten für vorhandene Systeme",
      features: [
        "eCommerce-Plattformen – KI-gestützte Personalisierung & dynamische Preisgestaltung",
        "Unternehmensportale – intelligente Suchfunktionen & Content-Empfehlungen",
        "KundenCenter-Anwendungen – Chatbots & automatisierte Kundeninteraktion",
        "Sales- & Vertriebsplattformen – KI-gestützte Lead-Generierung & Forecasting"
      ],
      benefits: ["Nahtlose Integration", "Sofortige Verbesserungen", "ROI-optimiert"]
    },
    {
      icon: TrendingUp,
      title: "KI für höhere Umsätze",
      subtitle: "Effizienz & Potenzialausschöpfung",
      features: [
        "Umsatzsteigerung durch KI-gestützte Kundeninteraktion & Personalisierung",
        "Effizienzsteigerung durch Automatisierung & optimierte Prozesse",
        "Potenzialausschöpfung durch bessere Nutzung bestehender Daten"
      ],
      benefits: ["Höhere Umsätze", "Mehr Effizienz", "Bessere Datennutzung"]
    }
  ];

  const whyBrovko = [
    {
      icon: Code,
      title: "Individuelle KI-Entwicklung",
      description: "Maßgeschneiderte Lösungen für spezifische Anforderungen"
    },
    {
      icon: Shield,
      title: "Private LLMs für Unternehmen",
      description: "KI ohne Datenschutzrisiken & DSGVO-konform"
    },
    {
      icon: Settings,
      title: "Nahtlose Integration",
      description: "Bestehende Plattformen mit KI-Funktionalitäten erweitern"
    },
    {
      icon: Target,
      title: "Maximaler Mehrwert",
      description: "Umsatzsteigerung, Effizienzgewinne & Potenzialausschöpfung"
    },
    {
      icon: Lock,
      title: "Höchste Sicherheitsstandards",
      description: "Unternehmensdaten bleiben im Unternehmen"
    },
    {
      icon: Shield,
      title: "Cybersecurity Maßnahmen",
      description: "Implementierung von Cybersecurity Maßnahmen – extern und intern"
    }
  ];

  const methodology = [
    {
      icon: Target,
      title: "Anforderungsanalyse",
      description: "Identifikation der passenden KI-Technologien für Ihr Unternehmen",
      phase: "Phase 1"
    },
    {
      icon: Code,
      title: "Individuelle Entwicklung",
      description: "Maßgeschneiderte KI-Anwendungen für Ihre Geschäftsprozesse",
      phase: "Phase 2"
    },
    {
      icon: Settings,
      title: "Integration & Optimierung",
      description: "Nahtlose Einbindung in bestehende Systeme",
      phase: "Phase 3"
    },
    {
      icon: Shield,
      title: "Skalierbarkeit & Sicherheit",
      description: "Zukunftssichere Lösungen mit höchster Datenschutzkonformität",
      phase: "Phase 4"
    }
  ];

  const useCases = [
    {
      industry: "E-Commerce",
      icon: ShoppingCart,
      applications: [
        "Personalisierte Produktempfehlungen",
        "Dynamische Preisgestaltung",
        "Intelligente Suchfunktionen",
        "Automatisierte Kundenbetreuung"
      ],
      impact: "20-30% Umsatzsteigerung"
    },
    {
      industry: "Kundenservice",
      icon: MessageSquare,
      applications: [
        "Intelligente Chatbots",
        "Automatisierte Ticketbearbeitung",
        "Sentiment-Analyse",
        "Predictive Customer Support"
      ],
      impact: "60% weniger Supportaufwand"
    },
    {
      industry: "Datenanalyse",
      icon: BarChart3,
      applications: [
        "Predictive Analytics",
        "Automatisierte Berichtserstellung",
        "Anomalie-Erkennung",
        "Business Intelligence"
      ],
      impact: "50% schnellere Insights"
    },
    {
      industry: "Interne Prozesse",
      icon: Cog,
      applications: [
        "Dokumentenmanagement",
        "Wissensmanagement",
        "Workflow-Automatisierung",
        "Intelligente Suche"
      ],
      impact: "40% Effizienzsteigerung"
    }
  ];

  const securityFeatures = [
    "End-to-End Verschlüsselung aller Datenübertragungen",
    "On-Premise oder Private Cloud Deployment",
    "DSGVO- und ISO-27001-konforme Implementierung",
    "Regelmäßige Sicherheitsaudits und Penetrationstests",
    "Rollenbasierte Zugriffskontrolle",
    "Compliance mit branchenspezifischen Anforderungen"
  ];

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <SEOHead {...seoConfig} />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-[46rem]">
            <div className="inline-flex items-center gap-2 bg-teal-400/10 text-brand-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Code className="h-4 w-4" />
              {t('kiAnwendungen.hero.badge')}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {t('kiAnwendungen.hero.title')}
            </h1>
            <p className="text-xl text-ink-muted mb-8 leading-relaxed">
              {t('kiAnwendungen.hero.description')}
            </p>
            <p className="text-lg text-ink-faint mb-12">
              {t('kiAnwendungen.hero.subtitle')}
            </p>
            <Button 
              className="bg-teal-400 hover:bg-brand text-ink-onBrand px-8 py-4 text-lg font-semibold"
              onClick={() => openContactModal("Hero Section", t('kiAnwendungen.hero.cta'))}
            >
              <Calendar className="mr-2 h-5 w-5" />
              {t('kiAnwendungen.hero.cta')}
            </Button>
          </div>
        </div>
      </section>

      {/* Why Brovko Section */}
      <section className="py-20 bg-surface-raised/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-12 text-center">
              {t('kiAnwendungen.whyBrovko.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {whyBrovko.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="flex items-start gap-4">
                    <div className="bg-teal-400/10 p-3 rounded-lg flex-shrink-0">
                      <IconComponent className="h-8 w-8 text-brand-400" />
                    </div>
                    <div>
                      <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-white mb-3">{item.title}</h3>
                      <p className="text-ink-muted leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-12 text-center">
              {t('kiAnwendungen.applications.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {applications.map((app, index) => {
                const IconComponent = app.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-8 border border-hairline hover:border-teal-400/50 transition-colors">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="bg-teal-400/10 p-3 rounded-lg">
                        <IconComponent className="h-8 w-8 text-brand-400" />
                      </div>
                      <div>
                        <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-white">{app.title}</h3>
                        <p className="text-ink-faint mt-1">{app.subtitle}</p>
                      </div>
                    </div>
                    
                    <ul className="space-y-3 mb-6">
                      {app.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-3">
                          <CheckCircle className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                          <span className="text-ink-muted text-sm leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="border-t border-hairline pt-4">
                      <div className="flex flex-wrap gap-2">
                        {app.benefits.map((benefit, bIndex) => (
                          <span key={bIndex} className="bg-teal-400/10 text-brand-400 px-3 py-1 rounded-full text-xs font-medium">
                            {benefit}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-slate-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-12 text-center">
              {t('kiAnwendungen.useCases.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {useCases.map((useCase, index) => {
                const IconComponent = useCase.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 rounded-lg p-6 border border-slate-700/30">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="bg-teal-400/10 p-3 rounded-lg">
                        <IconComponent className="h-6 w-6 text-brand-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-white">{useCase.industry}</h3>
                    </div>
                    <ul className="space-y-2 mb-4">
                      {useCase.applications.map((app, aIndex) => (
                        <li key={aIndex} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                          <span className="text-ink-muted text-sm leading-relaxed">{app}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center gap-2">
                      <Award className="h-4 w-4 text-brand-400" />
                      <span className="text-brand-400 text-sm font-medium">{useCase.impact}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-8 text-center">
              {t('kiAnwendungen.methodology.title')}
            </h2>
            <p className="text-xl text-ink-faint mb-12 text-center">
              {t('kiAnwendungen.methodology.subtitle')}
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              {methodology.map((method, index) => {
                const IconComponent = method.icon;
                return (
                  <div key={index} className="flex items-start gap-4">
                    <div className="bg-teal-400/10 p-3 rounded-lg flex-shrink-0">
                      <IconComponent className="h-8 w-8 text-brand-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="bg-teal-400 text-ink-onBrand px-2 py-1 rounded text-xs font-bold">{method.phase}</span>
                        <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-white">{method.title}</h3>
                      </div>
                      <p className="text-ink-muted leading-relaxed">{method.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-20 bg-surface-raised/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-8 text-center">
              {t('kiAnwendungen.security.title')}
            </h2>
            <p className="text-xl text-ink-faint mb-12 text-center">
              {t('kiAnwendungen.security.subtitle')}
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {securityFeatures.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Shield className="h-6 w-6 text-brand-400 mt-1 flex-shrink-0" />
                  <p className="text-ink-muted leading-relaxed">{feature}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-6">
              {t('kiAnwendungen.cta.title')}
            </h2>
            <p className="text-xl text-ink-muted mb-12">
              {t('kiAnwendungen.cta.description')}
            </p>
            <div className="flex justify-center">
              <Button 
                className="bg-teal-400 hover:bg-brand text-ink-onBrand px-8 py-4 text-lg font-semibold"
                onClick={() => openContactModal("CTA Section", t('kiAnwendungen.hero.cta'))}
              >
                <Calendar className="mr-2 h-5 w-5" />
                {t('kiAnwendungen.hero.cta')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        pageSource="KI-Anwendungen"
        buttonContext="KI-Anwendungen Service"
        buttonText="KI-Projekt starten - Gespräch buchen"
      />
    </div>
  );
};

export default KIAnwendungen;